// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useCallback, useMemo } from 'react';
import { useAccountWorkflowData } from '@shared/advisory/account-workflow/providers';
import { Translation } from '@shared/ui-kit/providers';
import { Table } from '@shared/ui-kit/components';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

export const useContractReview = () => {
  const {
    markStepAsCompleted,
    proposal,
    saveAdvisoryContractData,
    isUpdatingAdvisoryContract,
  } = useAccountWorkflowData();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const onNext = useCallback(async () => {
    try {
      await saveAdvisoryContractData();
      markStepAsCompleted('contract-review');
    } catch {
      enqueueSnackbar(t('errors.unhandled-error', { ns: 'errors' }), {
        variant: 'error',
      });
      return false;
    }
  }, [enqueueSnackbar, markStepAsCompleted, saveAdvisoryContractData, t]);

  const hasRetrocessions = useMemo(() => {
    return proposal?.advisory_type === 'non-independent';
  }, [proposal?.advisory_type]);

  const cards = useMemo(() => {
    return [
      {
        id: 'advisor',
        labelKey: 'contract-review.advisor.label',
        value: proposal?.advisor?.name || '-',
      },
      {
        id: 'service-type',
        labelKey: 'contract-review.service-type.label',
        value: (
          <Translation
            i18nKey={`activation-step2.advisory-types.${
              proposal?.advisory_type || 'non-independent'
            }.name`}
            ns="components"
          />
        ),
      },
      {
        id: 'commission',
        labelKey: 'contract-review.commission.label',
        value: (
          <>
            <Table.Data
              format="percentage"
              value={proposal?.commission_managed_assets ?? 0}
              formatOptions={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
            />
            &nbsp;
            <Translation
              i18nKey="contract-review.commission.annual"
              ns="components"
            />
          </>
        ),
      },
      {
        id: 'retrocessions',
        labelKey: 'contract-review.retrocessions.label',
        value: (
          <Translation
            i18nKey={`activation-step3.retrocession-types.${
              hasRetrocessions ? 'yes' : 'no'
            }.name`}
            ns="components"
          />
        ),
        explanationKey: hasRetrocessions
          ? 'contract-review.retrocessions.explanation'
          : 'contract-review.retrocessions.no-explanation',
      },
    ];
  }, [
    proposal?.advisor?.name,
    proposal?.advisory_type,
    proposal?.commission_managed_assets,
    hasRetrocessions,
  ]);

  return {
    onNext,
    proposal,
    cards,
    isLoading: isUpdatingAdvisoryContract,
  };
};
